import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  FormControlLabel,
  Checkbox,
  Typography,
  Grid,
  Divider,
  MenuItem,
} from '@mui/material';

const states = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

function AddressDetails({ data, onNext, onBack }) {
  const [formData, setFormData] = useState(data);
  const [errors, setErrors] = useState({
    billing: {},
    shipping: {}
  });

  const handleChange = (type, field) => (e) => {
    setFormData(prev => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: e.target.value
      }
    }));
  };

  const handleSameAsShipping = (e) => {
    setFormData(prev => ({
      ...prev,
      shipping: {
        ...prev.shipping,
        sameAsBilling: e.target.checked,
        ...(e.target.checked ? {
          street: prev.billing.street,
          city: prev.billing.city,
          state: prev.billing.state,
          zipCode: prev.billing.zipCode,
          country: prev.billing.country
        } : {})
      }
    }));
  };

  const validateAddress = (address, type) => {
    const newErrors = {};
    if (!address.street) newErrors.street = `${type} street is required`;
    if (!address.city) newErrors.city = `${type} city is required`;
    if (!address.state) newErrors.state = `${type} state is required`;
    if (!address.zipCode) newErrors.zipCode = `${type} ZIP code is required`;
    else if (!/^\d{5}(-\d{4})?$/.test(address.zipCode)) {
      newErrors.zipCode = 'Invalid ZIP code format';
    }
    return newErrors;
  };

  const validateForm = () => {
    const billingErrors = validateAddress(formData.billing, 'Billing');
    const shippingErrors = formData.shipping.sameAsBilling 
      ? {} 
      : validateAddress(formData.shipping, 'Shipping');

    setErrors({
      billing: billingErrors,
      shipping: shippingErrors
    });

    return Object.keys(billingErrors).length === 0 && 
           (formData.shipping.sameAsBilling || Object.keys(shippingErrors).length === 0);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onNext({ addresses: formData });
    }
  };

  const renderAddressFields = (type, disabled = false) => (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Street Address"
          name={`${type}-street`}
          value={formData[type].street}
          onChange={handleChange(type, 'street')}
          error={!!errors[type].street}
          helperText={errors[type].street}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="City"
          name={`${type}-city`}
          value={formData[type].city}
          onChange={handleChange(type, 'city')}
          error={!!errors[type].city}
          helperText={errors[type].city}
          disabled={disabled}
        />
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          select
          label="State"
          name={`${type}-state`}
          value={formData[type].state}
          onChange={handleChange(type, 'state')}
          error={!!errors[type].state}
          helperText={errors[type].state}
          disabled={disabled}
        >
          {states.map(state => (
            <MenuItem key={state} value={state}>
              {state}
            </MenuItem>
          ))}
        </TextField>
      </Grid>
      <Grid item xs={12} sm={3}>
        <TextField
          fullWidth
          label="ZIP Code"
          name={`${type}-zipCode`}
          value={formData[type].zipCode}
          onChange={handleChange(type, 'zipCode')}
          error={!!errors[type].zipCode}
          helperText={errors[type].zipCode}
          disabled={disabled}
        />
      </Grid>
    </Grid>
  );

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
      <Typography variant="h6" gutterBottom>
        Billing Address
      </Typography>
      {renderAddressFields('billing')}

      <Box sx={{ my: 3 }}>
        <Divider />
      </Box>

      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.shipping.sameAsBilling}
              onChange={handleSameAsShipping}
            />
          }
          label="Shipping address same as billing"
        />
      </Box>

      {!formData.shipping.sameAsBilling && (
        <>
          <Typography variant="h6" gutterBottom>
            Shipping Address
          </Typography>
          {renderAddressFields('shipping', false)}
        </>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
        <Button onClick={onBack}>
          Back
        </Button>
        <Button
          type="submit"
          variant="contained"
          color="primary"
        >
          Next
        </Button>
      </Box>
    </Box>
  );
}

export default AddressDetails; 